import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMissingGpcReport } from '../hooks/useDataQuality';
import { CalculatedAtCaption } from '../components/CalculatedAtCaption';

const PAGE_SIZE = 25;

/**
 * FR27: gepagineerd rapport van producten zonder GPC-mapping (kunnen helemaal niet gescoord
 * worden), met doorklik naar het bestaande product-/completeness-detail. Rendered as a
 * section on the client dashboard (h4-3 implementation note: "(of sectie)").
 */
export function MissingGpcReportPage({ gln }: { gln: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useMissingGpcReport(gln, page, PAGE_SIZE);
  const items = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (isLoading) {
    return <div data-testid="missing-gpc-report-skeleton" className="skeleton h-24 rounded-xl" />;
  }

  if (items.length === 0) {
    return (
      <div data-testid="missing-gpc-report-empty" className="rounded-xl p-4 text-xs" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', color: 'var(--foreground-muted)' }}>
        {t('dataQuality.missingGpc.empty')}
      </div>
    );
  }

  return (
    <div data-testid="missing-gpc-report" className="rounded-xl" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}>
      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <p className="text-xs font-semibold" style={{ color: 'var(--foreground-heading)' }}>
          {t('dataQuality.missingGpc.title', { count: total })}
        </p>
      </div>

      <ul>
        {items.map((item) => (
          <li key={item.cinXmlId} className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border-soft)' }}>
            <div>
              <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{t('dataQuality.missingGpc.gpcCode', { gpcCode: item.gpcCode })}</p>
              <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{item.cinXmlId}</p>
              <CalculatedAtCaption isoDate={item.laatsteEvaluatieDatum} />
            </div>
            <Link
              aria-label={t('dataQuality.missingGpc.openProduct', { cinXmlId: item.cinXmlId })}
              data-testid={`missing-gpc-open-${item.cinXmlId}`}
              to={`/data-quality/${item.gln}/products/${item.cinXmlId}`}
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[18px]" style={{ color: 'var(--foreground-muted)' }}>chevron_right</span>
            </Link>
          </li>
        ))}
      </ul>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3">
          <button
            className="text-xs font-medium disabled:opacity-40"
            data-testid="missing-gpc-prev-page"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{ color: 'var(--foreground-muted)' }}
            type="button"
          >
            {t('common.previous')}
          </button>
          <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
            {t('dataQuality.missingGpc.pagination', { page, totalPages })}
          </span>
          <button
            className="text-xs font-medium disabled:opacity-40"
            data-testid="missing-gpc-next-page"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            style={{ color: 'var(--foreground-muted)' }}
            type="button"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}
